import React, { useState, useEffect, useMemo } from 'react';
import { useFormik } from 'formik';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Breadcrums from '../../../components/company_panel/common/breadcrumbs';
import { capitalizeAllFirstLetters, getBrandId, getCompany } from '../../../helpers/commonFunctions';
import { addMenuItems, getAllMenuCategoryItems } from '../../../services/company_panel/categoryService'
import Swal from 'sweetalert2';
import { updateMenuCategoryOrder } from '../../../services/company_panel/menuCategoryService';

// A category, sub category or menu item that is switched off (status false). It
// can still be picked for the menu card, but customers will not see it until it
// is turned back on, so it is shown in red rather than hidden.
const isInactive = (category) => Boolean(category) && (category.status === false || category.status === 0 || category.status === '0');

const INACTIVE_TITLE = 'Switched off: it will not show on the menu until it is turned back on.';

// On a collapsed row, so what is switched off inside it is visible without
// opening it: sub categories on a category row, items on a sub category row.
const inactiveCountHint = (list, singular, plural) => {
    const count = (list || []).filter(isInactive).length;
    if (!count) return null;
    return <span className="menu-inactive-hint" title={INACTIVE_TITLE}>{count} inactive {count === 1 ? singular : plural}</span>;
};
const inactiveSubCategoriesHint = (category) => inactiveCountHint(category?.SubCategories, 'sub category', 'sub categories');
const inactiveItemsHint = (subCategory) => inactiveCountHint(subCategory?.menu_items, 'item', 'items');

const CreateMenu = () => {
    const [categories, setCategories] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState({});
    const [selectedSubCategories, setSelectedSubCategories] = useState({});
    const [selectedMenuItems, setSelectedMenuItems] = useState({});
    const [initialOrders, setInitialOrders] = useState({ categories: {}, subCategories: {} });
    const navigate = useNavigate();
    // page the user was on in the list (carried in the route) so we return there
    const params = useParams();
    const listPage = params?.pgno || 1;
    const [isBrandChanged, setIsBrandChanged] = useState(false);
    const [currentBrand] = useState(getBrandId());
    const [menuSearch, setMenuSearch] = useState('');

    const breadcrumbs = [
        { title: "Menu ", url: `/company/menu/menu/menu_card/list/${listPage}` },
        { title: "Create Menu", url: "" },
    ];

    useMemo(() => {
        document.addEventListener('brand-changed', function () {
            setIsBrandChanged(!isBrandChanged);
        });
        if (currentBrand != getBrandId()) {
            navigate(`/company/menu/menu/menu_card/list/${1}`);
        }
    });
    useEffect(() => {
        getAllMenuCategoryItems({ brand_id: getBrandId() })
            .then(data => {
                if (data?.data?.response) {
                    const menuItems = data.data.response;
                    const filteredCategories = menuItems.map(category => ({
                        ...category,
                        SubCategories: category.SubCategories.filter(sub => sub.menu_items.length > 0).map((subCategory) => ({ ...subCategory, fetching_order: subCategory.fetching_order ? subCategory.fetching_order : 0 })),
                        fetching_order: category.fetching_order ? category.fetching_order : 0
                    })).filter(category => category.SubCategories.length > 0);
                    setCategories(filteredCategories);

                    // Snapshot the default fetching_order so submit can send only the rows that changed.
                    const categoryOrders = {};
                    const subCategoryOrders = {};
                    filteredCategories.forEach((category) => {
                        categoryOrders[category.id] = category.fetching_order;
                        (category.SubCategories || []).forEach((sub) => {
                            subCategoryOrders[sub.id] = sub.fetching_order;
                        });
                    });
                    setInitialOrders({ categories: categoryOrders, subCategories: subCategoryOrders });
                }
            })
            .catch(error => console.log("error=====>", error));
    }, [isBrandChanged]);

    const handleCategoryChange = (categoryId) => {
        setSelectedCategories(prev => ({
            ...prev,
            [categoryId]: !prev[categoryId]
        }));
        if (!selectedCategories[categoryId]) {
            setSelectedSubCategories(prev => {
                const updatedSubCategories = { ...prev };
                const category = categories.find(cat => cat.id === categoryId);
                category.SubCategories.forEach(sub => {
                    updatedSubCategories[sub.id] = false;
                });
                return updatedSubCategories;
            });
            setSelectedMenuItems(prev => {
                const updatedMenuItems = { ...prev };
                const category = categories.find(cat => cat.id === categoryId);
                category.SubCategories.forEach(sub => {
                    sub.menu_items.forEach(menuItem => {
                        updatedMenuItems[menuItem.id] = false;
                    });
                });
                return updatedMenuItems;
            });
        }
    };

    const handleSubCategoryChange = (subCategoryId) => {
        setSelectedSubCategories(prev => ({
            ...prev,
            [subCategoryId]: !prev[subCategoryId]
        }));
        if (!selectedSubCategories[subCategoryId]) {
            setSelectedMenuItems(prev => {
                const updatedMenuItems = { ...prev };
                const category = categories.find(cat => cat.SubCategories.some(sub => sub.id === subCategoryId));
                const subCategory = category.SubCategories.find(sub => sub.id === subCategoryId);
                subCategory.menu_items.forEach(menuItem => {
                    updatedMenuItems[menuItem.id] = false;
                });
                return updatedMenuItems;
            });
        }
    };

    const handleMenuItemChange = (menuItemId) => {
        setSelectedMenuItems(prev => {
            const updatedMenuItems = {
                ...prev,
                [menuItemId]: !prev[menuItemId]
            };

            // Update Formik's value for menu_items_list
            const selectedItems = Object.keys(updatedMenuItems).filter(id => updatedMenuItems[id]);
            formik.setFieldValue('menu_items_list', selectedItems);

            return updatedMenuItems;
        });
    };

    const validationSchema = yup.object().shape({
        name: yup.string().trim().required('Name is required').max(20, 'Name must be 20 characters or less'),
        menu_items_list: yup.array().min(1, 'At least one item must be selected').required('This field is required'),
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: "",
            menu_items_list: [],
            // category_order: {} 
        },
        validationSchema: validationSchema,
        onSubmit: values => {
            let formData = new FormData();
            formData.append('name', values.name);
            formData.append('menu_items_list', JSON.stringify(values.menu_items_list));
            formData.append('brand_id', getBrandId());
            formData.append('company_id', getCompany().company_id);

            // On create we send a row for every category / subcategory: its own
            // fetching_order if the user changed it, otherwise 1. menu_id is sent
            // once at the top level, against the menu created below.
            const orderRows = [];
            categories.forEach((category) => {
                const categoryChanged = String(category.fetching_order) !== String(initialOrders.categories[category.id]);
                orderRows.push({
                    category_id: category.id,
                    sub_category_id: null,
                    fetching_order: categoryChanged ? category.fetching_order : 1,
                });
                (category.SubCategories || []).forEach((sub) => {
                    const subChanged = String(sub.fetching_order) !== String(initialOrders.subCategories[sub.id]);
                    orderRows.push({
                        category_id: null,
                        sub_category_id: sub.id,
                        fetching_order: subChanged ? sub.fetching_order : 1,
                    });
                });
            });

            // Create the menu first so we have a menu_id, then persist the
            // per-menu category order against the newly created menu.
            addMenuItems(formData).then(response => {
                if (response.data.status === 1 || response.data.status === "1") {
                    const newMenuId = response.data.response?.id;
                    if (orderRows.length) {
                        updateMenuCategoryOrder({ menu_id: newMenuId, categories: orderRows, operation_type: 'create_menu' })
                            .catch((error) => {
                                console.log("Error===>", error)
                            });
                    }
                    Swal.fire({
                        customClass: 'swal-wide',
                        icon: 'success',
                        title: response.data.message,
                        showConfirmButton: false,
                        timer: 1500,
                        toast: true,
                        position: 'top-right',
                    });
                    setTimeout(() => {
                        navigate(`/company/menu/menu/menu_card/list/${listPage}`);
                    }, 1000);
                } else {
                    Swal.fire({
                        customClass: 'swal-wide',
                        icon: 'error',
                        title: response.data.message,
                        showConfirmButton: false,
                        timer: 5000,
                        toast: true,
                        position: 'top-right',
                    });
                }
            }).catch(error => {
                console.log("error ====> ", error);
            });
        }
    });


    // One box searches all three levels: a category, a sub category or an item,
    // by name (partial) or by id (exact). An empty box matches everything, so the
    // predicates below double as "show all" when nothing is typed.
    const searchTerm = menuSearch.trim().toLowerCase();
    const matchesSearch = (name, id) => !searchTerm
        || String(name ?? '').toLowerCase().includes(searchTerm)
        || String(id ?? '') === searchTerm;
    // A match higher up keeps everything below it, so hitting a category shows its
    // whole tree while hitting an item narrows its sub category to that item.
    const isCategoryMatched = (category) => matchesSearch(category?.name, category?.id);
    const isSubCategoryMatched = (category, subCategory) =>
        isCategoryMatched(category) || matchesSearch(subCategory?.name, subCategory?.id);
    const isMenuItemVisible = (category, subCategory, menuItem) =>
        isSubCategoryMatched(category, subCategory) || matchesSearch(menuItem?.item_name, menuItem?.id);
    const isSubCategoryVisible = (category, subCategory) =>
        isSubCategoryMatched(category, subCategory)
        || (subCategory?.menu_items || []).some(menuItem => matchesSearch(menuItem?.item_name, menuItem?.id));
    const isCategoryVisible = (category) =>
        isCategoryMatched(category)
        || (category?.SubCategories || []).some(subCategory => isSubCategoryVisible(category, subCategory));
    // Counts behind the result chips, and the flag the empty state hangs off.
    const searchResults = categories.reduce((counts, category) => {
        if (!isCategoryVisible(category)) return counts;
        counts.categories += 1;
        (category?.SubCategories || []).forEach(subCategory => {
            if (!isSubCategoryVisible(category, subCategory)) return;
            counts.subCategories += 1;
            counts.menuItems += (subCategory?.menu_items || [])
                .filter(menuItem => isMenuItemVisible(category, subCategory, menuItem)).length;
        });
        return counts;
    }, { categories: 0, subCategories: 0, menuItems: 0 });
    const hasSearchResults = searchResults.categories > 0;

    // Wrap the matched part of a name so the hit stands out in a long tree.
    const highlightMatch = (text) => {
        const label = capitalizeAllFirstLetters(text);
        if (!searchTerm) return label;
        const index = String(label).toLowerCase().indexOf(searchTerm);
        if (index < 0) return label;
        return <>
            {label.slice(0, index)}
            <mark className='menu-search-hit'>{label.slice(index, index + searchTerm.length)}</mark>
            {label.slice(index + searchTerm.length)}
        </>;
    };

    const handelCategoryOrderChanges = (categoryIndex, value) => {
        // let categoryOrder = {...formik.values.category_order}
        // categoryOrder[categoryIndex] = { id : categoryId, order : value}
        // formik.setFieldValue("category_order",categoryOrder);
        let tempCategories = [...categories];
        tempCategories[categoryIndex].fetching_order = value;
        setCategories(tempCategories);
    };

    const handelSubCategoryOrderChanges = (categoryIndex, subCategoryIndx, value) => {
        let tempCategories = [...categories];
        tempCategories[categoryIndex].SubCategories[subCategoryIndx].fetching_order = value;
        setCategories(tempCategories);
    }
    
    return (
        <>
            <div className="page-header d-flex align-items-center">
                <div className="page-header-left">
                    <Breadcrums data={breadcrumbs} />
                    <h4>Create Menu</h4>
                </div>
                <div className="page-header-right ms-auto"></div>
            </div>
            <form onSubmit={formik.handleSubmit}>
                <div className="row row-sm">
                    <div className="card custom-card">
                        <div className="card-body">
                            {categories?.length === 0 ?
                                <div className="alert alert-warning text-center">
                                    There is no item created yet, Please{' '}
                                    <Link to="/company/menu/menu/menu_items/add">
                                        create a menu item
                                    </Link>{' '}
                                    to create menu
                                </div> : null}

                            <div className="row row-sm">
                                <div className='col-lg-6 form-group mt-3'>
                                    <label htmlFor='name' className='text-left d-flex'>
                                        Name <span className="requirestar">*</span>
                                    </label>
                                    <input
                                        name='name'
                                        id="name"
                                        type='text'
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.name}
                                        placeholder='Enter name'
                                        className='form-control'
                                    />
                                    <span className='text-danger d-flex text-left'>
                                        {formik.errors.name && formik.touched.name && formik.errors.name}
                                    </span>
                                </div>
                                <div className='col-lg-6'></div>

                                <label className='col-lg-6 mt-4 ' style={{ fontWeight: '600' }}>Select Items</label>
                                <div className='col-lg-12 form-group mt-2 mb-3'>
                                    <div className='menu-search-wrap'>
                                        <i className='ti ti-search menu-search-icon'></i>
                                        <input
                                            type='text'
                                            className='form-control menu-search-input'
                                            value={menuSearch}
                                            onChange={(e) => setMenuSearch(e.target.value)}
                                            placeholder='Search category, sub category or item by name or id'
                                        />
                                        {menuSearch ?
                                            <button type='button' className='menu-search-clear' onClick={() => setMenuSearch('')} aria-label='Clear search'>
                                                <i className='ti ti-close'></i>
                                            </button> : null}
                                    </div>
                                    {searchTerm && hasSearchResults ?
                                        <div className='menu-search-meta'>
                                            <span className='menu-search-chip'>{searchResults.categories} {searchResults.categories === 1 ? 'category' : 'categories'}</span>
                                            <span className='menu-search-chip'>{searchResults.subCategories} {searchResults.subCategories === 1 ? 'sub category' : 'sub categories'}</span>
                                            <span className='menu-search-chip'>{searchResults.menuItems} {searchResults.menuItems === 1 ? 'item' : 'items'}</span>
                                        </div> : null}
                                </div>
                                {searchTerm && !hasSearchResults ?
                                    <div className='menu-search-empty'>
                                        <i className='ti ti-search'></i>
                                        <span>No category, sub category or item matches “{menuSearch.trim()}”</span>
                                    </div> : null}
                                {categories.map((category, index) => (
                                    !isCategoryVisible(category) ? null :
                                    <div key={category.id} className={`category-label${isInactive(category) ? ' menu-category-inactive' : ''}`}>
                                        <div className='d-flex align-items-center'>
                                            <label className='d-flex align-items-center cp mb-0' style={{ userSelect: 'none' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={!!selectedCategories[category.id]}
                                                    onChange={() => handleCategoryChange(category.id)}
                                                    className='form-check-input cp'
                                                />
                                                {category?.image && (
                                                    <img
                                                        src={category.image}
                                                        alt={category?.name || 'category'}
                                                        className='category-thumb mx-2'
                                                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                                    />
                                                )}
                                                <span className='form-check-label' title={isInactive(category) ? INACTIVE_TITLE : undefined}>{highlightMatch(category.name)}</span>{inactiveSubCategoriesHint(category)}
                                            </label>
                                            <input
                                                type="text"
                                                name='category_order'
                                                placeholder="Enter order"
                                                className="form-control form-control-sm mx-2"
                                                style={{ width: '85px', height: '18px' }}
                                                value={category?.fetching_order}
                                                onChange={(e) => handelCategoryOrderChanges(index, e.target.value)}
                                            />
                                        </div>
                                        {(selectedCategories[category.id] || searchTerm) && category.SubCategories.length > 0 && (
                                            <div className="subcategory-label mt-2">
                                                {category.SubCategories.map((subCategory, subCategoryIndx) => (
                                                    !isSubCategoryVisible(category, subCategory) ? null :
                                                    <div key={subCategory.id} className={isInactive(subCategory) ? 'menu-subcategory-inactive' : undefined}>
                                                        <div className='d-flex align-items-center mb-2'>
                                                            <label className='d-flex align-items-center cp mb-0' style={{ userSelect: 'none' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={!!selectedSubCategories[subCategory.id]}
                                                                    onChange={() => handleSubCategoryChange(subCategory.id)}
                                                                    className='form-check-input cp'
                                                                />
                                                                <span className='form-check-label' title={isInactive(subCategory) ? INACTIVE_TITLE : undefined}>{highlightMatch(subCategory.name)}</span>{inactiveItemsHint(subCategory)}
                                                            </label>
                                                            <input
                                                                type="text"
                                                                name='sub_category_order'
                                                                placeholder="Enter order"
                                                                className="form-control form-control-sm mx-2"
                                                                style={{ width: '90px', height: '18px' }}
                                                                value={subCategory?.fetching_order}
                                                                onChange={(e) => handelSubCategoryOrderChanges(index, subCategoryIndx, e.target.value)}
                                                            />
                                                        </div>
                                                        {(selectedSubCategories[subCategory.id] || searchTerm) && subCategory.menu_items.length > 0 && (
                                                            <div className="menu-item submenu-label">
                                                                {subCategory.menu_items.filter(menuItem => isMenuItemVisible(category, subCategory, menuItem)).map(menuItem => (
                                                                    <div key={menuItem.id} className={isInactive(menuItem) ? 'menu-chip-inactive' : undefined} title={isInactive(menuItem) ? INACTIVE_TITLE : undefined}>
                                                                        <label className='mt-3 cp'>
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={!!selectedMenuItems[menuItem.id]}
                                                                                onChange={() => handleMenuItemChange(menuItem.id)}
                                                                                className='form-check-input cp '
                                                                            />
                                                                            <span className='form-check-name'>{highlightMatch(menuItem.item_name)}</span>
                                                                        </label>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                                {formik.errors.menu_items_list && formik.touched.menu_items_list && (
                                    <span className='text-danger d-flex text-left'>
                                        {formik.errors.menu_items_list}
                                    </span>
                                )}
                                <div className="mt-2">
                                    <button className="btn btn-primary" type="submit">
                                        Create
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
            <ToastContainer
                position="top-right"
                hideProgressBar={false}
                autoClose={1000}
                newestOnTop={true}
                closeOnClick={false}
                draggable={false}
                rtl={false}
            />
        </>
    );
}

export default CreateMenu;
